/**
 * BackgroundJobTimeline Component
 * ORCA360 - Real-Time Background Job Status Display
 *
 * Displays status of background jobs (Evidence Validation, RCA, SMART Goals)
 * with real-time polling and visual indicators.
 *
 * Used by: SessionDetails, AssessmentGenerator
 */

import { useEffect, useState } from 'react';
import {
  useBackgroundJobStatus,
  getJobStatusIcon,
  getJobStatusColor,
  getJobStatusMessage,
  type BackgroundJobStatus,
  type JobStatus,
} from '../hooks/useBackgroundJobStatus';

/**
 * Component props
 */
interface BackgroundJobTimelineProps {
  sessionId: string;
  assessmentType: 'pilot' | 'instructor';
  showTitle?: boolean;
  compact?: boolean;
  /** Change this value to trigger an immediate status refresh */
  refreshKey?: number;
  /** Override validation status (e.g., when span-based extraction is used, we know it's verbatim immediately) */
  validationStatusOverride?: JobStatus;
}

/**
 * Individual job status item
 */
interface JobStatusItemProps {
  label: string;
  status: JobStatus;
  completedAt: string | null;
  error: string | null;
  compact?: boolean;
}

function JobStatusItem({ label, status, completedAt, error, compact = false }: JobStatusItemProps) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime] = useState(Date.now());

  // Track elapsed time for in-progress jobs
  useEffect(() => {
    if (status !== 'in_progress') {
      return;
    }

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [status, startTime]);

  const formatElapsedTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const icon = getJobStatusIcon(status);
  const colorClasses = getJobStatusColor(status);
  const message = getJobStatusMessage(status, completedAt);

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border ${colorClasses}`}>
        <span>{icon}</span>
        <span className="font-medium">{label}</span>
        {status === 'in_progress' && elapsedTime > 0 && (
          <span className="text-xs opacity-75">({formatElapsedTime(elapsedTime)})</span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${colorClasses} transition-all`}>
      <span className="text-lg leading-none mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold truncate">{label}</span>
          {status === 'in_progress' && elapsedTime > 0 && (
            <span className="text-xs font-medium opacity-75 whitespace-nowrap">
              {formatElapsedTime(elapsedTime)} elapsed
            </span>
          )}
        </div>
        <div className="text-xs mt-0.5 opacity-75">{message}</div>
        {error && (
          <div className="text-xs mt-1 text-red-700 bg-red-100 px-2 py-1 rounded">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main timeline component
 */
export function BackgroundJobTimeline({
  sessionId,
  assessmentType,
  showTitle = true,
  compact = false,
  refreshKey = 0,
  validationStatusOverride,
}: BackgroundJobTimelineProps) {
  const { pilot, instructor, isLoading, hasInitiallyLoaded, error, refresh } = useBackgroundJobStatus(sessionId, true);

  // Trigger immediate refresh when refreshKey changes (e.g., after inline validation completes)
  // Note: We intentionally exclude 'refresh' from deps to prevent infinite loops
  // The refresh function's identity changes on each render, but we only want to 
  // trigger when refreshKey actually changes
  useEffect(() => {
    if (refreshKey > 0) {
      console.log('[BackgroundJobTimeline] Refresh triggered by refreshKey change:', refreshKey);
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Select the appropriate status based on assessment type
  const baseStatus: BackgroundJobStatus = assessmentType === 'pilot' ? pilot : instructor;

  // Apply validation status override if provided (e.g., when span-based extraction confirms verbatim)
  const status: BackgroundJobStatus = validationStatusOverride
    ? {
        ...baseStatus,
        validationStatus: validationStatusOverride,
        validationCompletedAt: validationStatusOverride === 'verbatim' ? new Date().toISOString() : baseStatus.validationCompletedAt,
      }
    : baseStatus;

  // Don't show anything if all jobs are pending (not started yet)
  const hasStarted =
    status.validationStatus !== 'pending' ||
    status.rcaStatus !== 'pending' ||
    status.smartStatus !== 'pending';

  // Show loading skeleton while waiting for initial data fetch
  // This prevents showing default "Pending" status before we know the actual status
  if (isLoading || !hasInitiallyLoaded) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-steel/10 rounded w-48 mb-2"></div>
        <div className="space-y-2">
          <div className="h-10 bg-steel/5 rounded"></div>
          <div className="h-10 bg-steel/5 rounded"></div>
          <div className="h-10 bg-steel/5 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-red-700">
          <span className="text-lg">⚠️</span>
          <span className="text-sm font-medium">Error loading background job status</span>
        </div>
        <div className="text-xs text-red-600 mt-1 ml-7">{error}</div>
      </div>
    );
  }

  if (!hasStarted) {
    return null; // Don't show timeline if no jobs have started
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        <JobStatusItem
          label={status.validationStatus === 'verbatim' ? 'Verbatim' : 'Validation'}
          status={status.validationStatus}
          completedAt={status.validationCompletedAt}
          error={status.validationError}
          compact
        />
        <JobStatusItem
          label={assessmentType === 'instructor' ? 'Development' : 'RCA'}
          status={status.rcaStatus}
          completedAt={status.rcaCompletedAt}
          error={null}
          compact
        />
        <JobStatusItem
          label={assessmentType === 'instructor' ? 'Prof. Goals' : 'SMART Goals'}
          status={status.smartStatus}
          completedAt={status.smartCompletedAt}
          error={null}
          compact
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {showTitle && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-navy">🔄 Background Processing</span>
          {status.allCompleted && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
              All Complete
            </span>
          )}
          {status.isAnyProcessing && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium animate-pulse">
              Processing...
            </span>
          )}
          {status.hasErrors && (
            <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full font-medium">
              Errors Detected
            </span>
          )}
        </div>
      )}

      <div className="space-y-2">
        <JobStatusItem
          label={status.validationStatus === 'verbatim' ? 'Evidence Verbatim' : 'Evidence Validation'}
          status={status.validationStatus}
          completedAt={status.validationCompletedAt}
          error={status.validationError}
        />
        <JobStatusItem
          label={assessmentType === 'instructor' ? 'Development Areas Analysis' : 'Root Cause Analysis'}
          status={status.rcaStatus}
          completedAt={status.rcaCompletedAt}
          error={null}
        />
        <JobStatusItem
          label={assessmentType === 'instructor' ? 'Professional Development Goals' : 'SMART Goals Generation'}
          status={status.smartStatus}
          completedAt={status.smartCompletedAt}
          error={null}
        />
      </div>

      {/* Helper text */}
      {status.isAnyProcessing && (
        <div className="text-xs text-steel/60 italic mt-3 pl-3 border-l-2 border-steel/20">
          Background jobs are running on the server. You can safely navigate away or close this page.
        </div>
      )}

      {status.allCompleted && (
        <div className="text-xs text-green-600 italic mt-3 pl-3 border-l-2 border-green-300">
          All background processing complete! Assessment is ready for review.
        </div>
      )}
    </div>
  );
}

/**
 * Compact variant for inline display
 */
export function BackgroundJobBadges({
  sessionId,
  assessmentType,
}: {
  sessionId: string;
  assessmentType: 'pilot' | 'instructor';
}) {
  return (
    <BackgroundJobTimeline sessionId={sessionId} assessmentType={assessmentType} showTitle={false} compact />
  );
}
